import { useState, useEffect, useCallback, useRef } from 'react';
import { is_dev } from '../helpers/environment';

const POLL_INTERVAL = 60_000; // 60 seconds
const SNOOZE_DURATION = 15 * 60_000; // 15 minutes

interface VersionCheckResult {
  updateAvailable: boolean;
  snooze: () => void;
}

async function hashText(text: string): Promise<string> {
  const buffer = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export const useVersionCheck = (): VersionCheckResult => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  const baselineHash = useRef<string | null>(null);

  const snooze = useCallback(() => {
    setSnoozedUntil(Date.now() + SNOOZE_DURATION);
    setUpdateAvailable(false);
  }, []);

  useEffect(() => {
    if (is_dev()) return;

    const checkVersion = async () => {
      if (snoozedUntil && Date.now() < snoozedUntil) return;
      if (snoozedUntil && Date.now() >= snoozedUntil) {
        setSnoozedUntil(null);
      }

      try {
        const response = await fetch(`/?_t=${Date.now()}`, {
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) return;
        const html = await response.text();
        const hash = await hashText(html);

        if (!baselineHash.current) {
          baselineHash.current = hash;
        } else if (hash !== baselineHash.current) {
          setUpdateAvailable(true);
        }
      } catch {
        // Silently ignore fetch errors
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [snoozedUntil]);

  return { updateAvailable, snooze };
};
